import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { ApiError, authApi, storageKey, useAuth } from '@invite/app-core';
import { useT } from '../i18n/useT';
import { twoFactorSession } from './twoFactorSession';

export const DEFAULT_RETURN_URL = '/';

/** Only same-origin paths are honoured; `//x`, `/\x` (browsers treat it as `//x`) and `/%5Cx` fall back. */
export const normalizeReturnUrl = (value: string | null | undefined): string => {
  if (value === undefined || value === null || value.trim().length === 0) {
    return DEFAULT_RETURN_URL;
  }
  if (!value.startsWith('/') || value.startsWith('//') || /\\|%5c/i.test(value)) {
    return DEFAULT_RETURN_URL;
  }
  try {
    return new URL(value, window.location.origin).origin === window.location.origin
      ? value
      : DEFAULT_RETURN_URL;
  } catch {
    return DEFAULT_RETURN_URL;
  }
};

/** Shared tail of every successful factor: sign in, then go to returnUrl. */
export const useCompleteLogin = (): ((
  token: string,
  tokenExpiryDate: string,
  returnUrl: string
) => Promise<void>) => {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  return useCallback(
    async (token, tokenExpiryDate, returnUrl) => {
      signIn(token, new Date(tokenExpiryDate));
      await navigate(returnUrl);
      twoFactorSession.clear();
    },
    [signIn, navigate]
  );
};

interface LoginFlow {
  submit: (email: string, password: string, returnUrl: string) => Promise<void>;
  error: string | null;
  busy: boolean;
}

export const useLoginFlow = (): LoginFlow => {
  const t = useT();
  const navigate = useNavigate();
  const completeLogin = useCompleteLogin();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = useCallback(
    async (email: string, password: string, returnUrl: string) => {
      setError(null);
      if (email.trim().length === 0 || password.length === 0) {
        setError(t('Email and password are required.'));
        return;
      }
      setBusy(true);
      try {
        let clientDeviceId: string | undefined;
        try {
          clientDeviceId = localStorage.getItem(storageKey('clientDeviceId')) ?? undefined;
        } catch {
          clientDeviceId = undefined;
        }
        const result = await authApi.login(email, password, clientDeviceId);
        if (result.tokenType === 'two_factor') {
          twoFactorSession.set({
            token: result.token,
            returnUrl,
            phone: result.phone ?? null,
            verificationType:
              result.verificationType === 'sms'
                ? 'sms'
                : result.verificationType === 'app'
                  ? 'app'
                  : undefined
          });
          await navigate(
            result.twoFactorConfigurationRequired === true
              ? '/login/two-factor/configure'
              : '/login/two-factor/verify'
          );
          return;
        }
        await completeLogin(result.token, result.tokenExpiryDate, returnUrl);
      } catch (caught) {
        if (caught instanceof ApiError && caught.status < 500) {
          setError(
            caught.status === 401 || caught.status === 403
              ? t('Login failed. Please try again.')
              : caught.message
          );
        } else {
          setError(t('Unable to sign in right now. Please try again.'));
        }
      } finally {
        setBusy(false);
      }
    },
    [t, navigate, completeLogin]
  );

  return { submit, error, busy };
};
